import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useUser } from "../lib/userAuth";
import { Loader2 } from "lucide-react";

export default function QuestionsSection({ slug }) {
  const { user, signIn } = useUser();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [activeAnswer, setActiveAnswer] = useState(null);
  const [answerText, setAnswerText] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/waterfalls/${slug}/questions`);
      setItems(r.data.items || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  const ask = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post(`/waterfalls/${slug}/questions`, { text });
      setText("");
      load();
    } finally { setBusy(false); }
  };

  const answer = async (qid) => {
    if (!answerText.trim()) return;
    setBusy(true);
    try {
      await api.post(`/questions/${qid}/answers`, { text: answerText });
      setAnswerText("");
      setActiveAnswer(null);
      load();
    } finally { setBusy(false); }
  };

  return (
    <section className="py-10 border-t border-stone-200" data-testid="questions-section">
      <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight text-stone-900 mb-6">Questions &amp; answers</h2>

      {user ? (
        <form onSubmit={ask} className="rounded-2xl border border-stone-200 p-4 mb-8 flex gap-2" data-testid="ask-form">
          <input
            value={text} onChange={(e) => setText(e.target.value)}
            placeholder="Ask the community a question about this waterfall…"
            maxLength={1000}
            data-testid="ask-input"
            className="flex-1 bg-transparent outline-none px-3 py-2 text-stone-900 placeholder:text-stone-400"
          />
          <button type="submit" disabled={busy} className="btn-at btn-at-dark text-sm disabled:opacity-50" data-testid="ask-submit-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ask"}
          </button>
        </form>
      ) : (
        <button onClick={() => signIn(null, "Sign in to ask a question and get answers from people who've actually been there.")} className="btn-at btn-at-dark text-sm mb-8" data-testid="signin-to-ask-btn">
          Sign in to ask a question
        </button>
      )}

      {loading ? (
        <p className="text-stone-500">Loading questions…</p>
      ) : items.length === 0 ? (
        <p className="text-stone-500 italic">No questions yet — be the first to ask.</p>
      ) : (
        <div className="space-y-6" data-testid="questions-list">
          {items.map((q) => (
            <article key={q.id} className="border-l-2 border-stone-200 pl-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-semibold text-stone-900">{q.user_name}</span>
                <span className="text-stone-300">·</span>
                <span className="text-xs text-stone-500">{new Date(q.created_at).toLocaleDateString()}</span>
              </div>
              <p className="text-stone-800">{q.text}</p>
              <div className="mt-3 pl-4 space-y-3">
                {q.answers?.map((a) => (
                  <div key={a.id} className="text-sm">
                    <span className="font-semibold text-stone-900">{a.user_name}: </span>
                    <span className="text-stone-700">{a.text}</span>
                  </div>
                ))}
                {user ? (
                  activeAnswer === q.id ? (
                    <div className="flex gap-2 pt-1">
                      <input
                        autoFocus
                        value={answerText} onChange={(e) => setAnswerText(e.target.value)}
                        placeholder="Your answer…"
                        data-testid={`answer-input-${q.id}`}
                        className="flex-1 bg-stone-50 rounded-md px-3 py-1.5 text-sm outline-none focus:bg-white focus:ring-2 focus:ring-stone-900"
                      />
                      <button onClick={() => answer(q.id)} disabled={busy} className="text-sm font-semibold text-stone-900" data-testid={`answer-submit-${q.id}`}>
                        Post
                      </button>
                    </div>
                  ) : (
                    <button onClick={() => { setActiveAnswer(q.id); setAnswerText(""); }} className="text-sm text-stone-500 hover:text-stone-900 underline" data-testid={`answer-trigger-${q.id}`}>
                      Answer
                    </button>
                  )
                ) : null}
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
